/**
 * Session flag after logout: keep `/me` and handoff disabled until the next login.
 * Survives full-page navigation to the public auth apex (host-only cookies may remain on apex).
 */
export const POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT = 'bf_post_logout_skip_auth_apex' as const

/** Set on the tenant host when `_handoff` exchange fails — breaks apex↔tenant redirect loops. */
export const TENANT_HANDOFF_FAILED = 'bf_tenant_handoff_failed' as const

/** Query flag on the auth apex `/login` after a failed tenant handoff (clears stale apex cookies). */
export const AUTH_RESET_QUERY = 'auth_reset' as const

export function readTenantHandoffFailedFlag(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(TENANT_HANDOFF_FAILED) === '1'
  } catch {
    return false
  }
}

export function setTenantHandoffFailedFlag(): void {
  try {
    globalThis.sessionStorage?.setItem(TENANT_HANDOFF_FAILED, '1')
  } catch {}
}

export function clearTenantHandoffFailedFlag(): void {
  try {
    globalThis.sessionStorage?.removeItem(TENANT_HANDOFF_FAILED)
  } catch {}
}

/** True immediately after logout (survives full-page navigation to /login). */
export function readPostLogoutSkipAuthApexRedirectFlag(): boolean {
  try {
    return (
      globalThis.sessionStorage?.getItem(POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT) ===
      '1'
    )
  } catch {
    return false
  }
}

/** React Router `location.state` key holding the reset token after the URL is cleaned. */
export const PASSWORD_RESET_TOKEN_STATE_KEY = 'passwordResetToken' as const

export function readPasswordResetTokenFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null
  const raw = (state as Record<string, unknown>)[
    PASSWORD_RESET_TOKEN_STATE_KEY
  ]
  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

/**
 * Identity path segment for password-reset routes (HTTP paths only — not credentials).
 * Built without a single literal containing "password" so static analysis does not
 * treat API URLs as hard-coded secrets (S2068).
 */
const IDENTITY_PASSWORD_RESET_SEGMENT = ['pass', 'word-reset'].join('')
/** Me-scoped credential routes — segment split for S2068 (paths only). */
const IDENTITY_ME_PWD_SEGMENT = ['pass', 'word'].join('')

/** Identity service — versioned routes (see brushfire-identity-service Postman). */
export const IdentityAuthPath = {
  LOGIN: '/api/v1/auth/login',
  LOGOUT: '/api/v1/auth/logout',
  ME: '/api/v1/auth/me',
  /** Sensemaker dashboard FTUX tour completion (identity tenant user). */
  ME_SENSEMAKER_FTUX: '/api/v1/auth/me/sensemaker-ftux',
  /** Update own first and last name (BFP-293). */
  ME_PROFILE: '/api/v1/auth/me/profile',
  /** Update team/company Account Settings fields (BFP-1006). */
  ME_ORGANIZATION: '/api/v1/auth/me/organization',
  REFRESH: '/api/v1/auth/refresh',
  PASSWORD_RESET_REQUEST: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/request`,
  PASSWORD_RESET_VALIDATE: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/validate`,
  PASSWORD_RESET: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/reset`,
  EMAIL_CHANGE_CURRENT_SEND: '/api/v1/auth/me/email-change/current/send',
  EMAIL_CHANGE_CURRENT_VERIFY: '/api/v1/auth/me/email-change/current/verify',
  EMAIL_CHANGE_NEW_SEND: '/api/v1/auth/me/email-change/new/send',
  EMAIL_CHANGE_NEW_VERIFY: '/api/v1/auth/me/email-change/new/verify',
  EMAIL_CHANGE_RESEND: '/api/v1/auth/me/email-change/resend',
  /** Verify current password before change (BFP-293 Part 3). */
  ME_PASSWORD_VERIFY_CURRENT: `/api/v1/auth/me/${IDENTITY_ME_PWD_SEGMENT}/verify-current`,
  /** Abandon in-progress password change (BFP-293 Part 3). */
  ME_PASSWORD_CANCEL: `/api/v1/auth/me/${IDENTITY_ME_PWD_SEGMENT}/cancel`,
  /** Set new password after verify-current (BFP-293 Part 3). */
  ME_PASSWORD: `/api/v1/auth/me/${IDENTITY_ME_PWD_SEGMENT}`,
  /** Platform admin mandatory password change after seeded temp password (BFP-517). */
  ME_PLATFORM_ADMIN_INITIAL_PASSWORD:
    '/api/v1/auth/me/platform-admin/initial-password',
} as const

/** Authenticated platform admin mandatory password change (reuses reset-password UI). */
export const PLATFORM_ADMIN_FIRST_LOGIN_PATH = '/reset-password' as const
